import { TranslatePipe } from "@ngx-translate/core";
import { AngularSvgIconModule } from "angular-svg-icon";

import { ChangeDetectionStrategy, Component, EventEmitter, computed, input, linkedSignal } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { SelfServiceCommandKeys } from "../../../helpers/enums/selfServiceCommands";
import { StartLoadMessage } from "../../../helpers/self-service/models/types";
import { ModifyBfdCommand } from "../../../helpers/self-service/modify_bfd";
import { SelfServiceCommand } from "../../../helpers/self-service/self-service-command";
import { IpBgpSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { SelfserviceToggle } from "../selfservice-toggle/selfservice-toggle";
import { SelfserviceWizard } from "../selfservice-wizard/selfservice-wizard";

type IpBgpSap = IpBgpSubscriptionDetails["saps"][number];

@Component({
  selector: "selfservice-bfd",
  templateUrl: "selfservice-bfd.html",
  styleUrl: "selfservice-bfd.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AngularSvgIconModule, FormsModule, SelfserviceToggle, SelfserviceWizard, TranslatePipe],
})
export class SelfserviceBfd {
  protected subscription = input.required<IpBgpSubscriptionDetails>();
  protected instance = input.required<IpBgpSap>();
  protected sapIndex = input.required<number>();
  protected bus = input.required<EventEmitter<StartLoadMessage>>();

  protected portDescription = computed(() => this.instance().port?.customerDescription);

  protected bfdActivated = linkedSignal(() => this.instance().bfd ?? false);
  protected timeInterval = linkedSignal(() => this.instance().bfdMinimumInterval);
  protected multiplier = linkedSignal(() => this.instance().bfdMultiplier);

  protected validTimeInterval = computed(() => {
    const minTimeInterval = 300;
    const maxTimeInterval = 10000;
    const t = this.timeInterval() ?? 0;
    return t >= minTimeInterval && t <= maxTimeInterval;
  });

  protected validMultiplier = computed(() => {
    const minMultiplier = 3;
    const maxMultiplier = 10;
    const m = this.multiplier() ?? 0;
    return m >= minMultiplier && m <= maxMultiplier;
  });

  protected canContinue = computed(() =>
    this.bfdActivated() ? this.validTimeInterval() && this.validMultiplier() : true,
  );

  protected readonly buildCommand = () => {
    const action = SelfServiceCommandKeys.ModifyBfd;
    const modify = new ModifyBfdCommand();
    modify.setForInstance(
      this.instance().subscriptionInstanceId,
      this.bfdActivated(),
      this.timeInterval() ?? 0,
      this.multiplier() ?? 0,
    );
    const command = new SelfServiceCommand(this.subscription().subscriptionId, action);
    command.payload = modify;
    return { command, action };
  };

  protected handleChange() {
    this.timeInterval.set(900);
    this.multiplier.set(3);
  }
}
